import { GridSortDirection, GridSortModel } from '@mui/x-data-grid';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { MAX_ENTITIES_NUMBER_PER_REQUEST, NUMBER_OF_RETRIES } from 'common/constants/exportData';
import { getNumberOfRequests, mergeResults } from 'common/utils/exportDataApiHelper';
import { mapUserMembershipDetails } from 'common/utils/userMembershipMapper';

import { APIError } from '..';
import { extractStatusCode } from '../apiHelpers';

import {
    APIClaimUserMembershipDelete,
    APIClaimUserMembershipGetList,
    APIClaimUserMembershipPost,
    APIClaimUserMembershipPostParams,
    APIClaimUserMembershipPostResponse,
    APIClaimUserMembershipPut,
    APIClaimUserMembershipPutParams,
    APIClaimUserMembershipPutResponse,
    ClaimUserMembershipGetPaginatedResponse,
    IUser } from './claimUserMembership.api';

export type IUserExtended = IUser & {
    claimId: string;
    claimName: string;
    value: string;
};

const  _formatClaimUserMembershipResponseWithPaginationInfo = async (item: ClaimUserThunkPostParams | ClaimUserThunkPutParams) => {
    const gridData = await APIClaimUserMembershipGetList({
        tenantId: item.tenantId,
        claimId: item.claimId,
        skip: item.paginationInfo.skip,
        take: item.paginationInfo.take,
        search: item.paginationInfo.search,
        sortKey: item.paginationInfo.sortModel?.[0]?.field,
        sortOrder: item.paginationInfo.sortModel?.[0]?.sort,
        status: item.status
    });
    const { count, skipped, taken, data } = gridData;
    const rows = formatClaimUserMembershipData(data);
    return {
        rows,
        count,
        skipped,
        taken,
    };
};

export const formatClaimUserMembershipData = (rows: ClaimUserMembershipGetPaginatedResponse[]) =>
    rows.map((item) =>
    {
        return {
            ...item,
            ...mapUserMembershipDetails(item),
            claimValue: item?.value
        };
    });

export const fetchClaimUserMemberships = createAsyncThunk('claim-user/fetchClaimUserMembership', async (item: {
    tenantId: string,
    claimId: string,
    skip?: number,
    take?: number,
    search?: string,
    sortKey?: string,
    sortOrder?: GridSortDirection,
    status?:string[]
}) => {
    const response = await APIClaimUserMembershipGetList(item);
    return {
        data: formatClaimUserMembershipData(response.data),
        skipped: response.skipped,
        taken: response.taken,
        count: response.count,
    };
});

export const fetchClaimUserMembershipsForExport = createAsyncThunk('user-groups/fetchClaimUserMembershipsForExport', async (item: {
    tenantId: string,
    claimId: string,
    skip?: number,
    take?: number,
    search?: string,
    sortKey?: string,
    sortOrder?: GridSortDirection,
    rowsCount?: number,
    signal: AbortSignal,
    status?:string[]
 }) => {
    const numberOfRequests = getNumberOfRequests(item.rowsCount);
    const fetchPromises = Array.from({ length: numberOfRequests }, async (_, i) => APIClaimUserMembershipGetList({
        ...item,
        skip: i * MAX_ENTITIES_NUMBER_PER_REQUEST,
        take: MAX_ENTITIES_NUMBER_PER_REQUEST,
        retries: NUMBER_OF_RETRIES
    }));

    const response = (await Promise.all(fetchPromises))
        .map((response)=>{
            return {
                data: formatClaimUserMembershipData(response.data),
                skipped: response.skipped,
                taken: response.taken
            };
        });

    return mergeResults(response);
});

export type ClaimUserThunkPostParams = APIClaimUserMembershipPostParams & {
    paginationInfo: {
        skip: number;
        take: number;
        search: string;
        sortModel: GridSortModel | undefined;
    },
    status?:string[]
}

export type ClaimUserThunkPostResponse = {
    item: ClaimUserThunkPostParams,
    response: APIClaimUserMembershipPostResponse,
    rows: IUserExtended[],
    count: number,
    skipped: number,
    taken: number
}

export const postClaimUserMembership = createAsyncThunk('claim-user/postClaimUserMembership', async (item: ClaimUserThunkPostParams) => {
    const response = await APIClaimUserMembershipPost(item);
    const paginationInfo = await _formatClaimUserMembershipResponseWithPaginationInfo(item);
    return {
        item,
        response,
        rows: paginationInfo.rows,
        count: paginationInfo.count,
        skipped: paginationInfo.skipped,
        taken: paginationInfo.taken
    };
});

export type ClaimUserThunkPutParams = APIClaimUserMembershipPutParams & {
    paginationInfo: {
        skip: number;
        take: number;
        search: string;
        sortModel: GridSortModel | undefined;
    },
    status?: string[]
}

export type ClaimUserThunkPutResponse = {
    item: ClaimUserThunkPutParams,
    response: APIClaimUserMembershipPutResponse,
    rows: IUserExtended[],
    count: number,
    skipped: number,
    taken: number,
}

export const putClaimUserMembership = createAsyncThunk('claim-user/putClaimUserMembership', async (item: ClaimUserThunkPutParams) => {
    const response = await APIClaimUserMembershipPut(item);
    const paginationInfo = await _formatClaimUserMembershipResponseWithPaginationInfo(item);
    return {
        item,
        response,
        rows: paginationInfo.rows,
        count: paginationInfo.count,
        skipped: paginationInfo.skipped,
        taken: paginationInfo.taken
    };
});

export const deleteClaimUserMembership = createAsyncThunk('claimUserMembership/deleteClaimUserMembership', async (item: {
    tenantId: string,
    claimId: string,
    userId: string,
    skip?: number,
    take?: number,
    search?: string;
    sortModel?: GridSortModel,
    status?:string[]
}) => {
    try {
        await APIClaimUserMembershipDelete(item);
    }
    catch (error) {
        if (!(error instanceof APIError)) {
            throw error;
        }
        const code = extractStatusCode(error.message);
        if (code !== 404) {
            throw error;
        }
    }

    try {
        const gridData = await APIClaimUserMembershipGetList({
            tenantId: item.tenantId,
            claimId: item.claimId,
            skip: item.skip,
            take: item.take,
            search: item.search,
            sortKey: item.sortModel?.[0]?.field,
            sortOrder: item.sortModel?.[0]?.sort,
            status: item.status
        });
        const { count, skipped, taken, data } = gridData;
        const rows = formatClaimUserMembershipData(data);
        return {
            item,
            rows,
            count,
            skipped,
            taken,
        };
    }
    catch(error) {
        if (!(error instanceof APIError)) {
            throw error;
        }
        const code = extractStatusCode(error.message);
        if(code === 404) {
            return {
                item,
                rows: [],
                count: 0,
                skipped: item.skip ?? 0,
                taken: item.take
            };
        }
        throw error;
    }
});
